/**
 * Repositorio del modo demostracion: implementa el contrato completo contra
 * los datos de ejemplo que viven en el navegador.
 *
 * Reproduce las reglas de negocio de la especificacion (validaciones, fusion
 * de categorias, podios, arrastre de temporada) para que la demo se comporte
 * como el producto y no como una maqueta muerta.
 */

import { MAX_FEATURED_BADGES } from '@/config/badges';
import { CATEGORY_RULES, WEIGHT_CLASSES } from '@/config/categories';
import { WEIGHT_MULTIPLIER } from '@/config/scoring';
import {
  assignWeightClass,
  mergeSmallCategories,
  weightChangeCooldown,
  locationChangeCooldown,
  type CategoryCount,
} from '@/lib/categories';
import { FORTNIGHTS_PER_SEASON, publishedStatusFor } from '@/lib/dates';
import { eligibleExercises } from '@/lib/exercises';
import { carryoverPoints, rankForPoints } from '@/lib/ranks';
import { computeScore } from '@/lib/scoring';
import { FORUM_MAX_LENGTH, summarizeReactions } from '@/lib/social';
import { parseVideoUrl } from '@/lib/videoUrl';
import type {
  AdminMetrics,
  AuditLogEntry,
  Badge,
  Competition,
  CompetitionTheme,
  Enrollment,
  Exercise,
  ForumPost,
  GenderCategory,
  Language,
  PersonalBest,
  Profile,
  PublicProfile,
  PublicSubmission,
  ReactionKind,
  SubmissionComment,
  RankingRow,
  Report,
  Season,
  Submission,
  UserBadge,
  WeightClass,
} from '@/types/domain';
import {
  RepositoryError,
  type AdminSubmissionFilter,
  type AdminUserUpdate,
  type AuthSession,
  type CompetitionFilter,
  type CreateProfileInput,
  type ProfileStats,
  type RankingQuery,
  type RankingResult,
  type RejectSubmissionInput,
  type Repository,
  type ScoreSubmissionInput,
  type SignUpInput,
  type StandingsGroup,
  type StandingsQuery,
  type SubmissionDetail,
  type SubmitMarkInput,
  type UpdateProfileInput,
  type UpsertCompetitionInput,
  type UpsertExerciseInput,
  type UpsertSeasonInput,
  type WeightClassThresholds,
} from '../types';
import type { DemoData } from './seed';
import { loadDemoData, loadDemoSession, mutate, saveDemoSession } from './store';

const DELAY_MS = 120;

/**
 * Una competicion publicada toma el estado que dicen sus fechas. Es la misma
 * regla que el disparador `competitions_status_by_dates` de la base: sin ella
 * la demo dejaria abrir a mano una competicion que aun no ha empezado.
 */
function statusByDates(
  competition: Pick<Competition, 'status' | 'starts_at' | 'submission_deadline'>,
): Competition['status'] {
  return competition.status === 'SCHEDULED' || competition.status === 'ACTIVE'
    ? publishedStatusFor(competition.starts_at, competition.submission_deadline)
    : competition.status;
}

/** Latencia simulada: sin ella los estados de carga no se pueden probar. */
function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), DELAY_MS));
}

/** Marca aprobada con sus reacciones y su recuento de comentarios. */
function toPublicSubmission(
  submission: Submission,
  data: DemoData,
  me: string | null,
): PublicSubmission {
  const author = data.profiles.find((p) => p.id === submission.user_id);
  const { counts, mine } = summarizeReactions(
    data.submissionReactions.filter((r) => r.submission_id === submission.id),
    me,
  );

  return {
    id: submission.id,
    competition_id: submission.competition_id,
    user_id: submission.user_id,
    alias: author?.alias ?? '',
    rank_level: author?.rank_level ?? 1,
    exercise_id: submission.exercise_id,
    platform: submission.platform,
    post_url: submission.post_url,
    final_score: submission.final_score ?? 0,
    created_at: submission.created_at,
    hidden: Boolean(submission.hidden_at),
    reactions: counts,
    my_reaction: mine,
    comment_count: data.submissionComments.filter(
      (c) => c.submission_id === submission.id && !c.hidden,
    ).length,
  };
}

function nowIso(): string {
  return new Date().toISOString();
}

function toPublic(profile: Profile): PublicProfile {
  // El peso corporal exacto y el email nunca salen de aqui (RF-PERF-03).
  return {
    id: profile.id,
    alias: profile.alias,
    avatar_url: profile.avatar_url,
    country: profile.country,
    city: profile.city,
    gender_category: profile.gender_category,
    weight_class: profile.weight_class,
    season_points: profile.season_points,
    rank_level: profile.rank_level,
    best_rank_level: profile.best_rank_level,
    privacy_level: profile.privacy_level,
    created_at: profile.created_at,
  };
}

/** Codigo de la insignia exclusiva. Vive en `src/config/badges.ts`. */
const SUMMIT_BADGE_CODE = 'the_summit';

export class DemoRepository implements Repository {
  readonly isDemo = true;

  private listeners = new Set<(session: AuthSession | null) => void>();

  // --- Autenticacion -------------------------------------------------------

  async getSession(): Promise<AuthSession | null> {
    const userId = loadDemoSession();
    if (!userId) return null;
    const data = loadDemoData();
    const profile = data.profiles.find((p) => p.id === userId);
    if (!profile) return null;

    // El correo sale de la cuenta, no del alias. Derivarlo del alias dejaba a
    // una cuenta recien creada —que todavia no tiene alias— con un correo
    // vacio, y entonces no habia forma de saber en que sesion estabas.
    const account = data.accounts?.find((a) => a.userId === userId);

    return {
      user: {
        id: profile.id,
        email: account?.email ?? `${profile.alias.toLowerCase() || 'cuenta'}@calipeak.demo`,
        emailVerified: profile.email_verified,
        provider: account?.provider ?? 'EMAIL',
      },
      profile,
    };
  }

  onAuthChange(listener: (session: AuthSession | null) => void): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private async emit(): Promise<void> {
    const session = await this.getSession();
    for (const listener of this.listeners) listener(session);
  }

  async signUpWithEmail(input: SignUpInput): Promise<AuthSession> {
    if (!input.acceptedTerms) throw new RepositoryError('mustAcceptTerms');
    if (!input.confirmedAge) throw new RepositoryError('mustConfirmAge');

    const id = `user-${Date.now()}`;
    const email = input.email.trim().toLowerCase();
    // Cuenta sin perfil: el onboarding lo crea en el paso siguiente.
    mutate((data) => {
      data.accounts = data.accounts ?? [];
      data.accounts.push({ userId: id, email, provider: 'EMAIL' });
      data.profiles.push({
        id,
        // Mismos valores provisionales que pone el disparador de la base
        // (migracion 20260101000800). La tabla exige alias con formato y peso
        // entre 30 y 200, asi que '' y 0 —lo que habia aqui— se rechazarian
        // en produccion. Los sustituye el onboarding en su primer paso.
        alias: `u_${id.slice(-8)}`,
        avatar_url: null,
        language: 'es',
        country: '',
        city: '',
        birth_date: null,
        gender_category: 'M',
        body_weight_kg: 70,
        weight_class: 'MIDDLE',
        weight_updated_at: null,
        location_updated_at: null,
        role: 'ATHLETE',
        season_points: 0,
        rank_level: 1,
        best_rank_level: 1,
        privacy_level: 'PUBLIC',
        instagram_account_id: null,
        tiktok_account_id: null,
        // En la demo el correo se da por verificado para no bloquear la prueba.
        email_verified: true,
        onboarding_completed: false,
        sounds_enabled: false,
        email_competitions: true,
        created_at: nowIso(),
        deleted_at: null,
      });
    });

    saveDemoSession(id);
    const session = (await this.getSession())!;
    void this.emit();
    return delay(session);
  }

  async signInWithEmail(email: string, _password: string): Promise<AuthSession> {
    const data = loadDemoData();
    const normalized = email.trim().toLowerCase();
    const account = data.accounts?.find((a) => a.email === normalized);
    const profile =
      (account && data.profiles.find((p) => p.id === account.userId)) ??
      data.profiles.find((p) => p.alias.toLowerCase() === normalized.split('@')[0]);

    if (!profile) throw new RepositoryError('invalidCredentials');
    saveDemoSession(profile.id);
    const session = (await this.getSession())!;
    void this.emit();
    return delay(session);
  }

  /**
   * Entrada con Google.
   *
   * En la demo no hay proveedor, pero antes esto metia a todo el mundo en
   * `demo-athlete` —una cuenta sembrada, con su alias y sus marcas— y por eso
   * no parecia tuya: es que no lo era. Ahora crea UNA cuenta propia la primera
   * vez y vuelve a ella despues, con su propio correo, y pasa por el onboarding
   * para que elijas alias, sexo y peso como en cualquier alta.
   */
  async signInWithGoogle(): Promise<AuthSession> {
    const existing = loadDemoData().accounts?.find((a) => a.provider === 'GOOGLE');
    if (existing) return this.demoSignInAs(existing.userId);

    const id = `user-google-${Date.now()}`;
    mutate((data) => {
      data.accounts = data.accounts ?? [];
      data.accounts.push({ userId: id, email: 'tu.cuenta@gmail.com', provider: 'GOOGLE' });
      data.profiles.push({
        id,
        // Mismos valores provisionales que pone el disparador de la base
        // (migracion 20260101000800). La tabla exige alias con formato y peso
        // entre 30 y 200, asi que '' y 0 —lo que habia aqui— se rechazarian
        // en produccion. Los sustituye el onboarding en su primer paso.
        alias: `u_${id.slice(-8)}`,
        avatar_url: null,
        language: 'es',
        country: '',
        city: '',
        birth_date: null,
        gender_category: 'M',
        body_weight_kg: 70,
        weight_class: 'MIDDLE',
        weight_updated_at: null,
        location_updated_at: null,
        role: 'ATHLETE',
        season_points: 0,
        rank_level: 1,
        best_rank_level: 1,
        privacy_level: 'PUBLIC',
        instagram_account_id: null,
        tiktok_account_id: null,
        // Google ya verifica el correo: no se pide otra vez.
        email_verified: true,
        onboarding_completed: false,
        sounds_enabled: false,
        email_competitions: true,
        created_at: nowIso(),
        deleted_at: null,
      });
    });

    saveDemoSession(id);
    const session = (await this.getSession())!;
    void this.emit();
    return delay(session);
  }

  async demoSignInAs(userId: string): Promise<AuthSession> {
    saveDemoSession(userId);
    const session = await this.getSession();
    if (!session) throw new RepositoryError('invalidCredentials');
    void this.emit();
    return delay(session);
  }

  demoAccounts() {
    return loadDemoData().demoAccounts.map(({ id, alias, role }) => ({ id, alias, role }));
  }

  async signOut(): Promise<void> {
    saveDemoSession(null);
    void this.emit();
    return delay(undefined);
  }

  async signOutEverywhere(): Promise<void> {
    return this.signOut();
  }

  async requestPasswordReset(): Promise<void> {
    return delay(undefined);
  }

  async resendVerificationEmail(): Promise<void> {
    return delay(undefined);
  }

  // --- Perfil --------------------------------------------------------------

  private requireUserId(): string {
    const userId = loadDemoSession();
    if (!userId) throw new RepositoryError('forbidden');
    return userId;
  }

  private requireProfile(): Profile {
    const userId = this.requireUserId();
    const profile = loadDemoData().profiles.find((p) => p.id === userId);
    if (!profile) throw new RepositoryError('forbidden');
    return profile;
  }

  private requireAdmin(): Profile {
    const profile = this.requireProfile();
    if (profile.role !== 'ADMIN' && profile.role !== 'MODERATOR') {
      throw new RepositoryError('forbidden');
    }
    return profile;
  }

  /**
   * Quien puede ver lo que moderacion ha ocultado: su autor y moderacion.
   *
   * En produccion esto lo decide la politica de la tabla (migraciones
   * 20260101001900 y 20260101002000). Aqui hay que escribirlo a mano, y tiene
   * que decir LO MISMO: si la demostracion ensena algo que la web real
   * esconde, la demostracion miente.
   */
  private puedeVerOculto(authorId: string): boolean {
    const userId = loadDemoSession();
    if (!userId) return false;
    if (userId === authorId) return true;
    const profile = loadDemoData().profiles.find((p) => p.id === userId);
    return profile?.role === 'ADMIN' || profile?.role === 'MODERATOR';
  }

  async createProfile(input: CreateProfileInput): Promise<Profile> {
    const userId = this.requireUserId();
    const alias = input.alias.trim();

    if (alias.length < 3 || alias.length > 20) throw new RepositoryError('aliasLength');
    if (!/^[\w-]+$/.test(alias)) throw new RepositoryError('aliasFormat');
    if (!(await this.isAliasAvailable(alias))) throw new RepositoryError('aliasTaken');

    const profile = mutate((data) => {
      const target = data.profiles.find((p) => p.id === userId);
      if (!target) throw new RepositoryError('forbidden');

      target.alias = alias;
      target.avatar_url = input.avatarUrl;
      target.language = input.language;
      target.country = input.country;
      target.city = input.city;
      target.birth_date = input.birthDate;
      target.gender_category = input.genderCategory;
      target.body_weight_kg = input.bodyWeightKg;
      target.weight_class = assignWeightClass(input.bodyWeightKg, input.genderCategory);
      target.weight_updated_at = nowIso();
      target.location_updated_at = nowIso();
      target.onboarding_completed = true;
      return target;
    });

    void this.emit();
    return delay(profile);
  }

  async getMyProfile(): Promise<Profile | null> {
    const userId = loadDemoSession();
    if (!userId) return null;
    return delay(loadDemoData().profiles.find((p) => p.id === userId) ?? null);
  }

  async updateMyProfile(input: UpdateProfileInput): Promise<Profile> {
    const userId = this.requireUserId();

    const profile = mutate((data) => {
      const target = data.profiles.find((p) => p.id === userId);
      if (!target) throw new RepositoryError('forbidden');

      if (input.alias !== undefined) target.alias = input.alias.trim();
      if (input.avatarUrl !== undefined) target.avatar_url = input.avatarUrl;
      if (input.language !== undefined) target.language = input.language;
      if (input.privacyLevel !== undefined) target.privacy_level = input.privacyLevel;
      if (input.soundsEnabled !== undefined) target.sounds_enabled = input.soundsEnabled;
      if (input.emailCompetitions !== undefined) {
        target.email_competitions = input.emailCompetitions;
      }
      if (input.onboardingCompleted !== undefined) {
        target.onboarding_completed = input.onboardingCompleted;
      }

      // Un cambio de peso cada 30 dias (RF-CAT-07).
      if (input.bodyWeightKg !== undefined && input.bodyWeightKg !== target.body_weight_kg) {
        const cooldown = weightChangeCooldown(target.weight_updated_at);
        if (!cooldown.allowed) {
          throw new RepositoryError('weightCooldown', { days: cooldown.daysRemaining });
        }
        target.body_weight_kg = input.bodyWeightKg;
        // RF-CAT-08: la categoria nueva no toca las competiciones en curso.
        // Las inscripciones ya creadas guardan su propia categoria, asi que
        // basta con actualizar el perfil.
        target.weight_class = assignWeightClass(input.bodyWeightKg, target.gender_category);
        target.weight_updated_at = nowIso();
      }

      // Un cambio de ubicacion cada 90 dias (RF-ONB-03).
      const changingLocation =
        (input.country !== undefined && input.country !== target.country) ||
        (input.city !== undefined && input.city !== target.city);
      if (changingLocation) {
        const cooldown = locationChangeCooldown(target.location_updated_at);
        if (!cooldown.allowed) {
          throw new RepositoryError('locationCooldown', { days: cooldown.daysRemaining });
        }
        if (input.country !== undefined) target.country = input.country;
        if (input.city !== undefined) target.city = input.city;
        target.location_updated_at = nowIso();
      }

      return target;
    });

    void this.emit();
    return delay(profile);
  }

  async isAliasAvailable(alias: string): Promise<boolean> {
    const userId = loadDemoSession();
    const normalized = alias.trim().toLowerCase();
    const taken = loadDemoData().profiles.some(
      (p) => p.id !== userId && p.alias.toLowerCase() === normalized,
    );
    return delay(!taken);
  }

  async getPublicProfile(alias: string): Promise<PublicProfile | null> {
    const normalized = alias.trim().toLowerCase();
    const profile = loadDemoData().profiles.find(
      (p) => p.alias.toLowerCase() === normalized && !p.deleted_at,
    );
    return delay(profile ? toPublic(profile) : null);
  }

  async getProfileStats(userId: string): Promise<ProfileStats> {
    const data = loadDemoData();
    const profile = data.profiles.find((p) => p.id === userId);
    if (!profile) throw new RepositoryError('notFound');

    const mySubmissions = data.submissions.filter(
      (s) => s.user_id === userId && s.status === 'APROBADO',
    );

    // Victorias: primeras posiciones en su categoria de competiciones cerradas.
    let competitionsWon = 0;
    for (const competition of data.competitions.filter((c) => c.status === 'PUBLISHED')) {
      const group = data.submissions.filter(
        (s) =>
          s.competition_id === competition.id &&
          s.status === 'APROBADO' &&
          s.gender_category === profile.gender_category &&
          s.weight_class === profile.weight_class,
      );
      if (group.length === 0) continue;
      const winner = [...group].sort((a, b) => (b.final_score ?? 0) - (a.final_score ?? 0))[0];
      if (winner.user_id === userId) competitionsWon += 1;
    }

    // Mejor marca por ejercicio (RF-PERF-01).
    const bestByExercise = new Map<string, PersonalBest>();
    for (const submission of mySubmissions) {
      const exercise = data.exercises.find((e) => e.id === submission.exercise_id);
      if (!exercise) continue;
      const current = bestByExercise.get(exercise.id);
      const score = submission.final_score ?? 0;
      if (!current || score > current.score) {
        bestByExercise.set(exercise.id, {
          exercise_id: exercise.id,
          exercise_code: exercise.code,
          reps: submission.declared_reps,
          seconds: submission.declared_seconds,
          added_weight_kg: submission.added_weight_kg,
          score,
          achieved_at: submission.reviewed_at ?? submission.created_at,
        });
      }
    }

    const globalOrder = [...data.profiles]
      .filter((p) => !p.deleted_at && p.onboarding_completed)
      .sort((a, b) => b.season_points - a.season_points);
    const globalPosition = globalOrder.findIndex((p) => p.id === userId);

    const localOrder = globalOrder.filter(
      (p) => p.country === profile.country && p.city === profile.city,
    );
    const localPosition = localOrder.findIndex((p) => p.id === userId);

    return delay({
      competitionsWon,
      approvedSubmissions: mySubmissions.length,
      streakFortnights: this.computeStreak(userId),
      globalPosition: globalPosition >= 0 ? globalPosition + 1 : null,
      localPosition: localPosition >= 0 ? localPosition + 1 : null,
      personalBests: [...bestByExercise.values()].sort((a, b) => b.score - a.score),
    });
  }

  /** Quincena en curso: la de las competiciones abiertas (no la programada). */
  private currentFortnightNumber(data: ReturnType<typeof loadDemoData>): number {
    const active = data.competitions.find((c) => c.status === 'ACTIVE');
    if (active) return active.fortnight_number;
    const published = data.competitions
      .filter((c) => c.status === 'PUBLISHED')
      .map((c) => c.fortnight_number);
    return published.length > 0 ? Math.max(...published) : 1;
  }

  /**
   * Quincenas consecutivas con al menos un envio, contando hacia atras.
   *
   * Se cuenta con un ordinal continuo entre temporadas, no con el numero de
   * quincena: ese vuelve a 1 cada trimestre, y la 6 de un trimestre y la 1 del
   * siguiente son seguidas. Mismo calculo que `get_profile_stats` en la base.
   */
  private computeStreak(userId: string): number {
    const data = loadDemoData();
    const ordinals = new Set(
      data.submissions
        .filter((s) => s.user_id === userId && s.status !== 'BORRADOR')
        .map((s) => {
          const competition = data.competitions.find((c) => c.id === s.competition_id);
          const season = data.seasons.find((x) => x.id === competition?.season_id);
          if (!competition || !season) return null;
          return (
            (season.year * 4 + season.quarter - 1) * FORTNIGHTS_PER_SEASON +
            competition.fortnight_number
          );
        })
        .filter((o): o is number => typeof o === 'number'),
    );
    if (ordinals.size === 0) return 0;

    const sorted = [...ordinals].sort((a, b) => b - a);
    let streak = 1;
    for (let i = 1; i < sorted.length; i += 1) {
      if (sorted[i] === sorted[i - 1] - 1) streak += 1;
      else break;
    }
    return streak;
  }

  async searchProfiles(query: string, limit = 10): Promise<PublicProfile[]> {
    const normalized = query.trim().toLowerCase();
    if (!normalized) return delay([]);
    const results = loadDemoData()
      .profiles.filter((p) => !p.deleted_at && p.alias.toLowerCase().includes(normalized))
      .slice(0, limit)
      .map(toPublic);
    return delay(results);
  }

  async exportMyData(): Promise<Record<string, unknown>> {
    const userId = this.requireUserId();
    const data = loadDemoData();
    return delay({
      exportedAt: nowIso(),
      profile: data.profiles.find((p) => p.id === userId),
      submissions: data.submissions.filter((s) => s.user_id === userId),
      enrollments: data.enrollments.filter((e) => e.user_id === userId),
      badges: data.userBadges.filter((b) => b.user_id === userId),
    });
  }

  async deleteMyAccount(): Promise<void> {
    const userId = this.requireUserId();
    mutate((data) => {
      const profile = data.profiles.find((p) => p.id === userId);
      if (profile) {
        profile.deleted_at = nowIso();
        profile.alias = `atleta_borrado_${profile.id.slice(-4)}`;
        profile.avatar_url = null;
        profile.city = '';
        profile.country = '';
      }
    });
    await this.signOut();
  }

  // --- Temporadas ----------------------------------------------------------

  async getCurrentSeason(): Promise<Season | null> {
    return delay(loadDemoData().seasons.find((s) => s.status === 'ACTIVE') ?? null);
  }

  async listSeasons(): Promise<Season[]> {
    return delay([...loadDemoData().seasons].sort((a, b) => b.start_date.localeCompare(a.start_date)));
  }

  // --- Competiciones -------------------------------------------------------

  async listCompetitions(filter: CompetitionFilter = {}): Promise<Competition[]> {
    const data = loadDemoData();
    let list = [...data.competitions];

    if (filter.seasonId) list = list.filter((c) => c.season_id === filter.seasonId);
    if (filter.theme) list = list.filter((c) => c.theme === filter.theme);
    if (filter.status === 'ACTIVE_OR_SCHEDULED') {
      list = list.filter((c) => c.status === 'ACTIVE' || c.status === 'SCHEDULED');
    } else if (filter.status) {
      list = list.filter((c) => c.status === filter.status);
    }

    list.sort((a, b) => b.starts_at.localeCompare(a.starts_at));
    if (filter.limit) list = list.slice(0, filter.limit);
    return delay(list);
  }

  async getCompetition(idOrSlug: string): Promise<Competition | null> {
    const data = loadDemoData();
    return delay(
      data.competitions.find((c) => c.id === idOrSlug || c.slug === idOrSlug) ?? null,
    );
  }

  async enroll(competitionId: string): Promise<Enrollment> {
    const profile = this.requireProfile();

    const enrollment = mutate((data) => {
      const competition = data.competitions.find((c) => c.id === competitionId);
      if (!competition) throw new RepositoryError('notFound');
      if (new Date(competition.submission_deadline) < new Date()) {
        throw new RepositoryError('submission.DEADLINE_PASSED');
      }

      const existing = data.enrollments.find(
        (e) => e.competition_id === competitionId && e.user_id === profile.id,
      );
      if (existing) return existing;

      const created: Enrollment = {
        id: `enr-${competitionId}-${profile.id}`,
        user_id: profile.id,
        competition_id: competitionId,
        // La categoria queda congelada aqui: un cambio de peso posterior no
        // afecta a esta competicion (RF-CAT-08).
        gender_category: profile.gender_category,
        weight_class: profile.weight_class,
        created_at: nowIso(),
      };
      data.enrollments.push(created);
      competition.participant_count += 1;
      return created;
    });

    return delay(enrollment);
  }

  async listMyEnrollments(): Promise<Enrollment[]> {
    const userId = loadDemoSession();
    if (!userId) return delay([]);
    return delay(loadDemoData().enrollments.filter((e) => e.user_id === userId));
  }

  async getStandings(query: StandingsQuery): Promise<StandingsGroup[]> {
    const data = loadDemoData();
    const competition = data.competitions.find((c) => c.id === query.competitionId);
    if (!competition) throw new RepositoryError('notFound');

    const published = competition.status === 'PUBLISHED';
    const relevant = data.submissions.filter(
      (s) =>
        s.competition_id === competition.id &&
        (published ? s.status === 'APROBADO' : s.status !== 'RECHAZADO' && s.status !== 'INVALIDADO'),
    );

    // Fusion de categorias por debajo del minimo (RF-CAT-06).
    const counts: CategoryCount[] = [];
    for (const gender of competition.active_categories) {
      for (const weightClass of ['LIGHT', 'MIDDLE', 'LIGHT_HEAVY', 'HEAVY'] as WeightClass[]) {
        counts.push({
          gender,
          weightClass,
          participants: relevant.filter(
            (s) => s.gender_category === gender && s.weight_class === weightClass,
          ).length,
        });
      }
    }
    const merges = mergeSmallCategories(counts);

    const groups: StandingsGroup[] = merges.map((merge) => {
      const rows = relevant
        .filter((s) => s.gender_category === merge.gender && merge.merged.includes(s.weight_class))
        .map((submission) => {
          const profile = data.profiles.find((p) => p.id === submission.user_id);
          return {
            submission,
            profile,
            score: published ? (submission.final_score ?? 0) : submission.provisional_score,
          };
        })
        .filter((entry) => entry.profile)
        .sort((a, b) => {
          // Desempate (RF-RANK-08): mayor puntuacion, luego envio mas temprano.
          if (b.score !== a.score) return b.score - a.score;
          return a.submission.created_at.localeCompare(b.submission.created_at);
        })
        .map<RankingRow>((entry, index) => ({
          user_id: entry.profile!.id,
          alias: entry.profile!.alias,
          avatar_url: entry.profile!.avatar_url,
          country: entry.profile!.country,
          city: entry.profile!.city,
          gender_category: entry.submission.gender_category,
          weight_class: entry.submission.weight_class,
          rank_level: entry.profile!.rank_level,
          position: index + 1,
          previous_position: null,
          points: entry.score,
          provisional: entry.submission.status !== 'APROBADO',
        }));

      return {
        genderCategory: merge.gender,
        weightClass: merge.resultingClass,
        mergedFrom: merge.merged.length > 1 ? merge.merged : [],
        rows,
      };
    });

    const filtered = groups.filter((group) => {
      if (query.genderCategory && query.genderCategory !== 'ALL') {
        if (group.genderCategory !== query.genderCategory) return false;
      }
      if (query.weightClass && query.weightClass !== 'ALL') {
        if (group.weightClass !== query.weightClass && !group.mergedFrom.includes(query.weightClass)) {
          return false;
        }
      }
      return true;
    });

    return delay(filtered);
  }

  // --- Ejercicios ----------------------------------------------------------

  async listExercises(): Promise<Exercise[]> {
    return delay(loadDemoData().exercises.filter((e) => e.active));
  }

  // --- Envios --------------------------------------------------------------

  async submitMark(input: SubmitMarkInput): Promise<Submission> {
    const profile = this.requireProfile();
    if (!profile.email_verified) throw new RepositoryError('emailNotVerified');

    const parsed = parseVideoUrl(input.postUrl);
    if (!parsed.ok || !parsed.value) {
      throw new RepositoryError(`video.${parsed.error ?? 'MALFORMED'}`);
    }
    const video = parsed.value;

    const submission = mutate((data) => {
      const competition = data.competitions.find((c) => c.id === input.competitionId);
      if (!competition) throw new RepositoryError('notFound');
      if (new Date(competition.submission_deadline) < new Date()) {
        throw new RepositoryError('submission.DEADLINE_PASSED');
      }

      const enrollment = data.enrollments.find(
        (e) => e.competition_id === competition.id && e.user_id === profile.id,
      );
      if (!enrollment) throw new RepositoryError('submission.NOT_ENROLLED');

      // Un mismo video no vale en dos competiciones ni para dos atletas
      // (RF-VID-04).
      const duplicate = data.submissions.find(
        (s) =>
          s.post_external_id === video.postExternalId &&
          !(s.user_id === profile.id && s.competition_id === competition.id),
      );
      if (duplicate) throw new RepositoryError('video.DUPLICATE');

      // El atleta no elige ejercicio: lo pone el revisor viendo el video. Aqui
      // se deja uno de los que pueden puntuar como marcador provisional —si la
      // competicion no fija lista, cualquiera de su tematica— y el revisor lo
      // corrige al puntuar.
      const exercise = eligibleExercises(competition, data.exercises)[0];
      if (!exercise) throw new RepositoryError('submission.EXERCISE_NOT_ALLOWED');

      // Un envio valido por competicion, sustituible hasta el cierre
      // (RF-COMP-04): se reemplaza el anterior en lugar de acumularlo.
      const existingIndex = data.submissions.findIndex(
        (s) => s.competition_id === competition.id && s.user_id === profile.id,
      );

      const created: Submission = {
        id: existingIndex >= 0 ? data.submissions[existingIndex].id : `sub-${Date.now()}`,
        user_id: profile.id,
        competition_id: competition.id,
        exercise_id: exercise.id,
        exercise_coefficient_snapshot: exercise.difficulty_coefficient,
        exercise_reference_snapshot: exercise.reference_mark,
        gender_category: enrollment.gender_category,
        weight_class: enrollment.weight_class,
        platform: video.platform,
        post_url: video.normalizedUrl,
        post_external_id: video.postExternalId,
        embed_html: null,
        // Sin marca declarada no hay nada que puntuar todavia: los rellena el
        // revisor al ver el video, y ahi nace la puntuacion.
        declared_reps: null,
        declared_seconds: null,
        added_weight_kg: null,
        body_weight_snapshot_kg: profile.body_weight_kg,
        weight_multiplier: 1,
        provisional_score: 0,
        final_score: null,
        score_breakdown_json: null,
        status: 'PENDIENTE',
        rejection_reason: null,
        rejection_comment: null,
        admin_note: null,
        reviewed_by: null,
        reviewed_at: null,
        auto_detected: false,
        notes: input.notes,
        created_at:
          existingIndex >= 0 ? data.submissions[existingIndex].created_at : nowIso(),
        updated_at: nowIso(),
      };

      if (existingIndex >= 0) data.submissions[existingIndex] = created;
      else data.submissions.push(created);

      return created;
    });

    return delay(submission);
  }

  async listMySubmissions(): Promise<Submission[]> {
    const userId = loadDemoSession();
    if (!userId) return delay([]);
    return delay(
      loadDemoData()
        .submissions.filter((s) => s.user_id === userId)
        .sort((a, b) => b.created_at.localeCompare(a.created_at)),
    );
  }

  async getMySubmissionFor(competitionId: string): Promise<Submission | null> {
    const userId = loadDemoSession();
    if (!userId) return delay(null);
    return delay(
      loadDemoData().submissions.find(
        (s) => s.user_id === userId && s.competition_id === competitionId,
      ) ?? null,
    );
  }

  async deleteSubmission(submissionId: string): Promise<void> {
    const userId = this.requireUserId();
    mutate((data) => {
      const index = data.submissions.findIndex(
        (s) => s.id === submissionId && s.user_id === userId,
      );
      if (index < 0) throw new RepositoryError('notFound');
      // Solo mientras este pendiente (RF-VID-09).
      if (data.submissions[index].status !== 'PENDIENTE') throw new RepositoryError('forbidden');
      data.submissions.splice(index, 1);
    });
    return delay(undefined);
  }

  // --- Rankings ------------------------------------------------------------

  async getRanking(query: RankingQuery): Promise<RankingResult> {
    const data = loadDemoData();
    const currentUserId = loadDemoSession();
    const pageSize = query.pageSize ?? 25;
    const page = query.page ?? 1;

    let effectiveScope = query.scope;
    let effectiveScopeKey = query.scopeKey ?? null;

    let candidates = data.profiles.filter((p) => !p.deleted_at && p.onboarding_completed);

    if (query.scope === 'COUNTRY' && query.scopeKey) {
      candidates = candidates.filter((p) => p.country === query.scopeKey);
    } else if (query.scope === 'CITY' && query.scopeKey) {
      const inCity = candidates.filter((p) => p.city === query.scopeKey);
      // Una ciudad con menos de 5 atletas se agrupa al pais (RF-RANK-10).
      if (inCity.length < CATEGORY_RULES.MIN_ATHLETES_FOR_CITY_RANKING) {
        const country = inCity[0]?.country;
        candidates = country ? candidates.filter((p) => p.country === country) : candidates;
        effectiveScope = country ? 'COUNTRY' : 'GLOBAL';
        effectiveScopeKey = country ?? null;
      } else {
        candidates = inCity;
      }
    }

    if (query.genderCategory && query.genderCategory !== 'ALL') {
      candidates = candidates.filter((p) => p.gender_category === query.genderCategory);
    }
    if (query.weightClass && query.weightClass !== 'ALL') {
      candidates = candidates.filter((p) => p.weight_class === query.weightClass);
    }
    if (query.rankLevel && query.rankLevel !== 'ALL') {
      candidates = candidates.filter((p) => p.rank_level === query.rankLevel);
    }
    if (query.search?.trim()) {
      const needle = query.search.trim().toLowerCase();
      candidates = candidates.filter((p) => p.alias.toLowerCase().includes(needle));
    }

    // Puntos del periodo pedido (RF-RANK-04).
    const pointsFor = (profile: Profile): number => {
      if (!query.period || query.period === 'SEASON' || query.period === 'ALL_TIME') {
        return profile.season_points;
      }
      // La quincena es la ronda en curso; un mes, aproximadamente las dos ultimas.
      const roundsBack = query.period === 'FORTNIGHT' ? 1 : 2;
      const currentFortnight = this.currentFortnightNumber(data);
      const relevantFortnights = new Set(
        Array.from({ length: roundsBack }, (_, i) => currentFortnight - i),
      );
      return data.submissions
        .filter(
          (s) =>
            s.user_id === profile.id &&
            s.status === 'APROBADO' &&
            relevantFortnights.has(
              data.competitions.find((c) => c.id === s.competition_id)?.fortnight_number ?? -1,
            ),
        )
        .reduce((sum, s) => sum + (s.final_score ?? 0), 0);
    };

    const scored = candidates
      .map((profile) => ({ profile, points: pointsFor(profile) }))
      .sort((a, b) => {
        if (b.points !== a.points) return b.points - a.points;
        // Desempate por alias para que el orden sea estable entre recargas.
        return a.profile.alias.localeCompare(b.profile.alias);
      });

    const rows: RankingRow[] = scored.map((entry, index) => ({
      user_id: entry.profile.id,
      alias: entry.profile.alias,
      avatar_url: entry.profile.avatar_url,
      country: entry.profile.country,
      city: entry.profile.city,
      gender_category: entry.profile.gender_category,
      weight_class: entry.profile.weight_class,
      rank_level: entry.profile.rank_level,
      position: index + 1,
      // La demo no guarda historico por quincenas: se simula una variacion estable
      // a partir del identificador, para poder ver las flechas del ranking.
      previous_position:
        index === 0 ? 1 : index + 1 + ((entry.profile.id.charCodeAt(6) % 5) - 2),
      points: entry.points,
      provisional: false,
    }));

    const start = (page - 1) * pageSize;
    return delay({
      rows: rows.slice(start, start + pageSize),
      total: rows.length,
      page,
      pageSize,
      currentUserRow: rows.find((r) => r.user_id === currentUserId) ?? null,
      effectiveScope,
      effectiveScopeKey,
      includesProvisional: false,
    });
  }

  // --- Insignias -----------------------------------------------------------

  async listBadges(): Promise<Badge[]> {
    return delay(loadDemoData().badges);
  }

  async listUserBadges(userId: string): Promise<UserBadge[]> {
    return delay(
      loadDemoData()
        .userBadges.filter((b) => b.user_id === userId)
        .sort((a, b) => b.awarded_at.localeCompare(a.awarded_at)),
    );
  }

  async setBadgeFeatured(userBadgeId: string, featured: boolean): Promise<void> {
    const userId = this.requireUserId();
    mutate((data) => {
      const target = data.userBadges.find((b) => b.id === userBadgeId && b.user_id === userId);
      if (!target) throw new RepositoryError('notFound');

      if (featured) {
        const count = data.userBadges.filter((b) => b.user_id === userId && b.featured).length;
        if (count >= MAX_FEATURED_BADGES) {
          throw new RepositoryError('maxFeatured', { max: MAX_FEATURED_BADGES });
        }
      }
      target.featured = featured;
    });
    return delay(undefined);
  }

  // --- Social --------------------------------------------------------------

  async follow(userId: string): Promise<void> {
    const me = this.requireUserId();
    mutate((data) => {
      if (data.follows.some((f) => f.follower_id === me && f.followed_id === userId)) return;
      data.follows.push({
        id: `follow-${me}-${userId}`,
        follower_id: me,
        followed_id: userId,
        created_at: nowIso(),
      });
    });
    return delay(undefined);
  }

  async unfollow(userId: string): Promise<void> {
    const me = this.requireUserId();
    mutate((data) => {
      const index = data.follows.findIndex(
        (f) => f.follower_id === me && f.followed_id === userId,
      );
      if (index >= 0) data.follows.splice(index, 1);
    });
    return delay(undefined);
  }

  async isFollowing(userId: string): Promise<boolean> {
    const me = loadDemoSession();
    if (!me) return delay(false);
    return delay(
      loadDemoData().follows.some((f) => f.follower_id === me && f.followed_id === userId),
    );
  }

  async createReport(
    targetType: Report['target_type'],
    targetId: string,
    reason: string,
  ): Promise<void> {
    const me = this.requireUserId();
    mutate((data) => {
      data.reports.push({
        id: `report-${Date.now()}`,
        reporter_id: me,
        target_type: targetType,
        target_id: targetId,
        reason,
        status: 'OPEN',
        created_at: nowIso(),
      });
    });
    return delay(undefined);
  }

  // --- Reacciones y comentarios ---------------------------------------------

  async listCompetitionSubmissions(competitionId: string): Promise<PublicSubmission[]> {
    const data = loadDemoData();
    const me = loadDemoSession();
    const rows = data.submissions
      .filter(
        (s) =>
          s.competition_id === competitionId &&
          s.status === 'APROBADO' &&
          (!s.hidden_at || this.puedeVerOculto(s.user_id)),
      )
      .sort((a, b) => (b.final_score ?? 0) - (a.final_score ?? 0))
      .map((submission) => toPublicSubmission(submission, data, me));

    return delay(rows);
  }

  async getPublicSubmission(submissionId: string): Promise<PublicSubmission | null> {
    const data = loadDemoData();
    const me = loadDemoSession();
    const submission = data.submissions.find(
      (s) =>
        s.id === submissionId &&
        s.status === 'APROBADO' &&
        (!s.hidden_at || this.puedeVerOculto(s.user_id)),
    );
    return delay(submission ? toPublicSubmission(submission, data, me) : null);
  }

  async setReaction(submissionId: string, kind: ReactionKind | null): Promise<void> {
    const me = this.requireUserId();
    mutate((data) => {
      // Una por persona y envio: se quita la anterior y, si hay clase nueva, se pone.
      data.submissionReactions = data.submissionReactions.filter(
        (r) => !(r.submission_id === submissionId && r.user_id === me),
      );
      if (kind) {
        data.submissionReactions.push({
          id: `reaction-${Date.now()}`,
          submission_id: submissionId,
          user_id: me,
          kind,
          created_at: nowIso(),
        });
      }
    });

    void this.emit();
    return delay(undefined);
  }

  async listComments(submissionId: string): Promise<SubmissionComment[]> {
    const data = loadDemoData();
    const me = loadDemoSession();
    const rows = data.submissionComments
      .filter((c) => c.submission_id === submissionId && (!c.hidden || c.author_id === me))
      .sort((a, b) => a.created_at.localeCompare(b.created_at));

    return delay(rows);
  }

  async addComment(submissionId: string, body: string): Promise<SubmissionComment> {
    const me = this.requireUserId();
    const text = body.trim();
    if (!text) throw new RepositoryError('required');
    if (text.length > 500) throw new RepositoryError('commentTooLong');

    const comment = mutate((data) => {
      const author = data.profiles.find((p) => p.id === me);
      const row: SubmissionComment = {
        id: `comment-${Date.now()}`,
        submission_id: submissionId,
        author_id: me,
        author_alias: author?.alias ?? '',
        author_rank_level: author?.rank_level ?? 1,
        body: text,
        hidden: false,
        created_at: nowIso(),
      };
      data.submissionComments.push(row);
      return row;
    });

    void this.emit();
    return delay(comment);
  }

  async deleteComment(commentId: string): Promise<void> {
    const me = this.requireUserId();
    mutate((data) => {
      data.submissionComments = data.submissionComments.filter(
        (c) => !(c.id === commentId && c.author_id === me),
      );
    });

    void this.emit();
    return delay(undefined);
  }

  // --- Foro de la comunidad ------------------------------------------------

  async listForumPosts(): Promise<ForumPost[]> {
    const data = loadDemoData();
    const rows = data.forumPosts
      .filter((post) => !post.hidden || this.puedeVerOculto(post.author_id))
      .sort((a, b) => b.created_at.localeCompare(a.created_at));

    return delay(rows);
  }

  async addForumPost(body: string, parentId?: string | null): Promise<ForumPost> {
    const me = this.requireUserId();
    const text = body.trim();
    if (!text) throw new RepositoryError('required');
    if (text.length > FORUM_MAX_LENGTH) throw new RepositoryError('forumPostTooLong');

    const post = mutate((data) => {
      const author = data.profiles.find((p) => p.id === me);
      const row: ForumPost = {
        id: `forum-${Date.now()}`,
        parent_id: parentId ?? null,
        author_id: me,
        author_alias: author?.alias ?? '',
        author_rank_level: author?.rank_level ?? 1,
        body: text,
        hidden: false,
        created_at: nowIso(),
      };
      data.forumPosts.push(row);
      return row;
    });

    void this.emit();
    return delay(post);
  }

  async deleteForumPost(postId: string): Promise<void> {
    const me = this.requireUserId();
    mutate((data) => {
      const mine = data.forumPosts.find((post) => post.id === postId && post.author_id === me);
      if (!mine) return;
      // Borrar un mensaje se lleva sus respuestas, igual que en la base.
      data.forumPosts = data.forumPosts.filter(
        (post) => post.id !== postId && post.parent_id !== postId,
      );
    });

    void this.emit();
    return delay(undefined);
  }

  // --- Contacto ------------------------------------------------------------

  /**
   * En demo el mensaje no se guarda: no hay nadie al otro lado que lo lea.
   * Se simula la espera para que el formulario se comporte igual.
   */
  async sendContactMessage(): Promise<void> {
    return delay(undefined);
  }

  // --- Integraciones sociales ----------------------------------------------

  async connectSocialAccount(platform: 'INSTAGRAM' | 'TIKTOK'): Promise<void> {
    const userId = this.requireUserId();
    mutate((data) => {
      const profile = data.profiles.find((p) => p.id === userId);
      if (!profile) return;
      if (platform === 'INSTAGRAM') profile.instagram_account_id = `ig_demo_${userId}`;
      else profile.tiktok_account_id = `tt_demo_${userId}`;
    });
    void this.emit();
    return delay(undefined);
  }

  async disconnectSocialAccount(platform: 'INSTAGRAM' | 'TIKTOK'): Promise<void> {
    const userId = this.requireUserId();
    mutate((data) => {
      const profile = data.profiles.find((p) => p.id === userId);
      if (!profile) return;
      if (platform === 'INSTAGRAM') profile.instagram_account_id = null;
      else profile.tiktok_account_id = null;
    });
    void this.emit();
    return delay(undefined);
  }

  async listAutoDetectedDrafts(): Promise<Submission[]> {
    const userId = loadDemoSession();
    if (!userId) return delay([]);
    return delay(
      loadDemoData().submissions.filter(
        (s) => s.user_id === userId && s.auto_detected && s.status === 'BORRADOR',
      ),
    );
  }

  // --- Administracion ------------------------------------------------------

  private detail(submission: Submission): SubmissionDetail | null {
    const data = loadDemoData();
    const athlete = data.profiles.find((p) => p.id === submission.user_id);
    const competition = data.competitions.find((c) => c.id === submission.competition_id);
    const exercise = data.exercises.find((e) => e.id === submission.exercise_id);
    if (!athlete || !competition || !exercise) return null;
    return { submission, athlete: toPublic(athlete), competition, exercise };
  }

  async adminListSubmissions(filter: AdminSubmissionFilter = {}): Promise<SubmissionDetail[]> {
    this.requireAdmin();
    const data = loadDemoData();

    let list = data.submissions.filter((s) => {
      if (filter.status === 'PENDING_ANY') {
        return s.status === 'PENDIENTE' || s.status === 'EN_REVISION';
      }
      if (filter.status) return s.status === filter.status;
      return true;
    });

    if (filter.competitionId) list = list.filter((s) => s.competition_id === filter.competitionId);
    if (filter.genderCategory) list = list.filter((s) => s.gender_category === filter.genderCategory);
    if (filter.weightClass) list = list.filter((s) => s.weight_class === filter.weightClass);
    if (filter.theme) {
      list = list.filter(
        (s) => data.competitions.find((c) => c.id === s.competition_id)?.theme === filter.theme,
      );
    }
    if (filter.search?.trim()) {
      const needle = filter.search.trim().toLowerCase();
      list = list.filter((s) => {
        const alias = data.profiles.find((p) => p.id === s.user_id)?.alias.toLowerCase() ?? '';
        return alias.includes(needle);
      });
    }

    const details = list
      .map((s) => this.detail(s))
      .filter((d): d is SubmissionDetail => Boolean(d));

    const sortBy = filter.sortBy ?? 'created_at';
    details.sort((a, b) => {
      switch (sortBy) {
        case 'competition':
          return a.competition.slug.localeCompare(b.competition.slug);
        case 'athlete':
          return a.athlete.alias.localeCompare(b.athlete.alias);
        case 'category':
          return `${a.submission.gender_category}${a.submission.weight_class}`.localeCompare(
            `${b.submission.gender_category}${b.submission.weight_class}`,
          );
        default:
          return a.submission.created_at.localeCompare(b.submission.created_at);
      }
    });

    return delay(filter.limit ? details.slice(0, filter.limit) : details);
  }

  async adminGetSubmission(submissionId: string): Promise<SubmissionDetail | null> {
    this.requireAdmin();
    const submission = loadDemoData().submissions.find((s) => s.id === submissionId);
    return delay(submission ? this.detail(submission) : null);
  }

  async adminScoreSubmission(input: ScoreSubmissionInput): Promise<Submission> {
    const admin = this.requireAdmin();
    if (input.manualAdjustment != null && !input.manualAdjustmentReason?.trim()) {
      // El motivo del ajuste manual es obligatorio (RF-PUNT-04).
      throw new RepositoryError('adjustReasonRequired');
    }

    const updated = mutate((data) => {
      const submission = data.submissions.find((s) => s.id === input.submissionId);
      if (!submission) throw new RepositoryError('notFound');

      const competition = data.competitions.find((c) => c.id === submission.competition_id);
      if (!competition) throw new RepositoryError('notFound');

      // El revisor puede fijar el ejercicio: el atleta ya no lo declara. Al
      // cambiarlo hay que volver a congelar coeficiente y referencia, que son
      // los del ejercicio realmente ejecutado (RF-PUNT-02).
      if (input.correctedExerciseId && input.correctedExerciseId !== submission.exercise_id) {
        const eligible = eligibleExercises(competition, data.exercises);
        if (!eligible.some((e) => e.id === input.correctedExerciseId)) {
          throw new RepositoryError('submission.EXERCISE_NOT_ALLOWED');
        }
        const chosen = data.exercises.find((e) => e.id === input.correctedExerciseId);
        if (!chosen) throw new RepositoryError('notFound');
        submission.exercise_id = chosen.id;
        submission.exercise_coefficient_snapshot = chosen.difficulty_coefficient;
        submission.exercise_reference_snapshot = chosen.reference_mark;
      }

      const exercise = data.exercises.find((e) => e.id === submission.exercise_id);
      if (!exercise) throw new RepositoryError('notFound');

      if (input.correctedReps !== undefined) submission.declared_reps = input.correctedReps;
      if (input.correctedSeconds !== undefined) submission.declared_seconds = input.correctedSeconds;
      if (input.correctedWeightKg !== undefined) {
        submission.added_weight_kg = competition.allows_weight ? input.correctedWeightKg : null;
      }

      const breakdown = computeScore({
        // Se usa el coeficiente congelado en el envio, no el vigente
        // (RF-PUNT-02): cambiar el catalogo no reescribe el pasado.
        difficulty: submission.exercise_coefficient_snapshot,
        reference: submission.exercise_reference_snapshot,
        theme: competition.theme,
        performance: {
          theme: competition.theme,
          reps: submission.declared_reps,
          seconds: submission.declared_seconds,
          enduranceFormat: competition.endurance_format,
          targetSeconds: competition.endurance_target_seconds,
        },
        addedWeightKg: submission.added_weight_kg,
        bodyWeightKg: submission.body_weight_snapshot_kg,
        bonuses: input.bonuses,
        competitionMultiplier: competition.score_multiplier,
        manualAdjustment: input.manualAdjustment,
        manualAdjustmentReason: input.manualAdjustmentReason,
      });

      const previous = {
        status: submission.status,
        final_score: submission.final_score,
      };

      submission.weight_multiplier = breakdown.weightMultiplier;
      submission.provisional_score = breakdown.total;
      submission.final_score = breakdown.total;
      submission.score_breakdown_json = breakdown;
      submission.status = 'APROBADO';
      submission.rejection_reason = null;
      submission.rejection_comment = null;
      submission.admin_note = input.adminNote ?? submission.admin_note;
      submission.reviewed_by = admin.id;
      submission.reviewed_at = nowIso();
      submission.updated_at = nowIso();

      this.recomputeUserPoints(data, submission.user_id);
      this.appendAudit(data, admin, 'SCORE_SUBMISSION', 'submission', submission.id, previous, {
        status: submission.status,
        final_score: submission.final_score,
      });

      return submission;
    });

    return delay(updated);
  }

  async adminRejectSubmission(input: RejectSubmissionInput): Promise<Submission> {
    const admin = this.requireAdmin();
    if (!input.comment.trim()) throw new RepositoryError('required');

    const updated = mutate((data) => {
      const submission = data.submissions.find((s) => s.id === input.submissionId);
      if (!submission) throw new RepositoryError('notFound');

      const previous = { status: submission.status, final_score: submission.final_score };

      submission.status = 'RECHAZADO';
      submission.rejection_reason = input.reason;
      submission.rejection_comment = input.comment.trim();
      submission.final_score = null;
      submission.reviewed_by = admin.id;
      submission.reviewed_at = nowIso();
      submission.updated_at = nowIso();

      this.recomputeUserPoints(data, submission.user_id);
      this.appendAudit(data, admin, 'REJECT_SUBMISSION', 'submission', submission.id, previous, {
        status: submission.status,
        rejection_reason: submission.rejection_reason,
      });

      return submission;
    });

    return delay(updated);
  }

  async adminFlagForSecondOpinion(submissionId: string, note: string): Promise<Submission> {
    const admin = this.requireAdmin();
    const updated = mutate((data) => {
      const submission = data.submissions.find((s) => s.id === submissionId);
      if (!submission) throw new RepositoryError('notFound');
      submission.status = 'EN_REVISION';
      submission.admin_note = note;
      submission.updated_at = nowIso();
      this.appendAudit(data, admin, 'FLAG_SECOND_OPINION', 'submission', submission.id, null, {
        admin_note: note,
      });
      return submission;
    });
    return delay(updated);
  }

  /** Recalcula puntos y rango de un atleta tras cambiar alguno de sus envios. */
  private recomputeUserPoints(
    data: ReturnType<typeof loadDemoData>,
    userId: string,
  ): void {
    const profile = data.profiles.find((p) => p.id === userId);
    if (!profile) return;

    const points = data.submissions
      .filter((s) => s.user_id === userId && s.status === 'APROBADO')
      .reduce((sum, s) => sum + (s.final_score ?? 0), 0);

    profile.season_points = points;
    profile.rank_level = rankForPoints(points).level;
    profile.best_rank_level = Math.max(profile.best_rank_level, profile.rank_level);

    // Cambiar los puntos de alguien puede cambiar quien va primero.
    this.reassignSummitBadge(data);
  }

  /**
   * Traspasa La Cima a quien encabeza el ranking global.
   *
   * Es la unica insignia que se quita. Todas las demas se ganan y se quedan;
   * esta la lleva UNA persona mientras sea la primera, y en cuanto alguien la
   * supera cambia de dueno. Por eso no basta con concederla al ganar: hay que
   * recalcularla cada vez que se mueven los puntos de cualquiera.
   *
   * Desempate: gana quien mas puntos tiene; si empatan, la conserva quien ya
   * la llevaba. Sin esa regla, dos atletas empatados se la robarian el uno al
   * otro en cada recalculo, y una insignia que parpadea no significa nada.
   */
  private reassignSummitBadge(data: ReturnType<typeof loadDemoData>): void {
    const badge = data.badges.find((b) => b.code === SUMMIT_BADGE_CODE);
    if (!badge) return;

    const holder = data.userBadges.find((b) => b.badge_id === badge.id);

    const candidates = data.profiles.filter((p) => !p.deleted_at && p.season_points > 0);
    if (candidates.length === 0) {
      // Nadie tiene puntos: la insignia no existe. Sin este caso, al vaciar la
      // temporada se quedaria colgada del ultimo que la tuvo.
      data.userBadges = data.userBadges.filter((b) => b.badge_id !== badge.id);
      return;
    }

    const leader = candidates.reduce((best, current) => {
      if (current.season_points !== best.season_points) {
        return current.season_points > best.season_points ? current : best;
      }
      // Empate: manda quien la tenga puesta.
      if (holder?.user_id === best.id) return best;
      if (holder?.user_id === current.id) return current;
      return best;
    });

    if (holder?.user_id === leader.id) return;

    data.userBadges = data.userBadges.filter((b) => b.badge_id !== badge.id);
    data.userBadges.push({
      id: `ub-summit-${leader.id}`,
      user_id: leader.id,
      badge_id: badge.id,
      competition_id: null,
      season_id: null,
      gender_category: leader.gender_category,
      weight_class: leader.weight_class,
      awarded_at: nowIso(),
      // Se destaca sola: es la insignia que uno quiere ensenar.
      featured: true,
    });
  }

  private appendAudit(
    data: ReturnType<typeof loadDemoData>,
    actor: Profile,
    action: string,
    entityType: string,
    entityId: string,
    before: unknown,
    after: unknown,
  ): void {
    data.auditLog.unshift({
      id: `audit-${Date.now()}-${Math.round(Math.random() * 1e6)}`,
      actor_id: actor.id,
      actor_alias: actor.alias,
      action,
      entity_type: entityType,
      entity_id: entityId,
      before_json: before,
      after_json: after,
      created_at: nowIso(),
    });
  }

  async adminUpsertCompetition(input: UpsertCompetitionInput): Promise<Competition> {
    const admin = this.requireAdmin();

    const result = mutate((data) => {
      if (input.id) {
        const existing = data.competitions.find((c) => c.id === input.id);
        if (!existing) throw new RepositoryError('notFound');
        const before = { ...existing };
        Object.assign(existing, input);
        existing.status = statusByDates(existing);
        this.applyThemeWeightPolicy(existing);
        this.appendAudit(data, admin, 'UPDATE_COMPETITION', 'competition', existing.id, before, {
          ...existing,
        });
        return existing;
      }

      const created: Competition = {
        id: `comp-${Date.now()}`,
        season_id: input.season_id ?? data.seasons.find((s) => s.status === 'ACTIVE')!.id,
        slug: input.slug ?? `competicion-${Date.now()}`,
        title_i18n: input.title_i18n ?? { es: '', en: '' },
        description_i18n: input.description_i18n ?? { es: '', en: '' },
        rules_i18n: input.rules_i18n ?? { es: '', en: '' },
        theme: input.theme ?? 'DYNAMIC',
        measurement_type: input.measurement_type ?? 'REPS',
        endurance_format: input.endurance_format ?? null,
        endurance_target_seconds: input.endurance_target_seconds ?? null,
        allows_weight: input.allows_weight ?? false,
        requires_weight: input.requires_weight ?? false,
        allowed_exercise_ids: input.allowed_exercise_ids ?? [],
        active_categories: input.active_categories ?? ['M', 'F', 'OPEN'],
        hashtag: input.hashtag ?? '#CaliPeak',
        cover_image_url: input.cover_image_url ?? null,
        starts_at: input.starts_at ?? nowIso(),
        submission_deadline: input.submission_deadline ?? nowIso(),
        results_published_at: null,
        score_multiplier: input.score_multiplier ?? 1,
        status: statusByDates({
          status: input.status ?? 'DRAFT',
          starts_at: input.starts_at ?? nowIso(),
          submission_deadline: input.submission_deadline ?? nowIso(),
        }),
        fortnight_number: input.fortnight_number ?? 1,
        participant_count: 0,
      };
      this.applyThemeWeightPolicy(created);
      data.competitions.push(created);
      this.appendAudit(data, admin, 'CREATE_COMPETITION', 'competition', created.id, null, created);
      return created;
    });

    return delay(result);
  }

  /** La tematica manda sobre la politica de lastre (RF-COMP-09). */
  private applyThemeWeightPolicy(competition: Competition): void {
    competition.allows_weight =
      competition.theme === 'STATIC' || competition.theme === 'DYNAMIC_WEIGHTED';
    competition.requires_weight = competition.theme === 'DYNAMIC_WEIGHTED';
  }

  async adminDeleteCompetition(competitionId: string): Promise<void> {
    const admin = this.requireAdmin();

    mutate((data) => {
      const competition = data.competitions.find((c) => c.id === competitionId);
      if (!competition) throw new RepositoryError('notFound');
      // Misma regla que la base: con un envio, en el estado que sea, no se borra.
      if (data.submissions.some((s) => s.competition_id === competitionId)) {
        throw new RepositoryError('competitionHasSubmissions');
      }

      data.competitions = data.competitions.filter((c) => c.id !== competitionId);
      data.enrollments = data.enrollments.filter((e) => e.competition_id !== competitionId);
      this.appendAudit(data, admin, 'DELETE_COMPETITION', 'competition', competitionId, {
        ...competition,
      }, null);
    });

    await delay(undefined);
  }

  async adminPublishResults(competitionId: string): Promise<void> {
    const admin = this.requireAdmin();

    mutate((data) => {
      const competition = data.competitions.find((c) => c.id === competitionId);
      if (!competition) throw new RepositoryError('notFound');

      // Todo lo que siga pendiente al publicar se queda fuera del podio.
      const approved = data.submissions.filter(
        (s) => s.competition_id === competitionId && s.status === 'APROBADO',
      );

      const groups = new Map<string, Submission[]>();
      for (const submission of approved) {
        const key = `${submission.gender_category}|${submission.weight_class}`;
        groups.set(key, [...(groups.get(key) ?? []), submission]);
      }

      const podiumCodes = ['laurel_crown', 'silver_shield', 'bronze_shield'];

      for (const [, group] of groups) {
        const ordered = [...group].sort((a, b) => {
          const diff = (b.final_score ?? 0) - (a.final_score ?? 0);
          return diff !== 0 ? diff : a.created_at.localeCompare(b.created_at);
        });

        ordered.slice(0, 3).forEach((submission, index) => {
          const badge = data.badges.find((b) => b.code === podiumCodes[index]);
          if (!badge) return;
          const id = `ub-${competition.id}-${submission.user_id}-${index}`;
          if (data.userBadges.some((b) => b.id === id)) return;
          data.userBadges.push({
            id,
            user_id: submission.user_id,
            badge_id: badge.id,
            competition_id: competition.id,
            season_id: competition.season_id,
            gender_category: submission.gender_category,
            weight_class: submission.weight_class,
            awarded_at: nowIso(),
            featured: false,
          });
        });
      }

      competition.status = 'PUBLISHED';
      competition.results_published_at = nowIso();

      for (const userId of new Set(approved.map((s) => s.user_id))) {
        this.recomputeUserPoints(data, userId);
      }

      this.appendAudit(
        data,
        admin,
        'PUBLISH_RESULTS',
        'competition',
        competition.id,
        { status: 'ACTIVE' },
        { status: 'PUBLISHED', results_published_at: competition.results_published_at },
      );
    });

    return delay(undefined);
  }

  async adminUpsertExercise(input: UpsertExerciseInput): Promise<Exercise> {
    const admin = this.requireAdmin();

    const result = mutate((data) => {
      if (input.id) {
        const existing = data.exercises.find((e) => e.id === input.id);
        if (!existing) throw new RepositoryError('notFound');
        const before = { ...existing };
        Object.assign(existing, input);
        // Cambiar el coeficiente sube la version; los envios ya puntuados
        // conservan el suyo (RF-PUNT-02).
        if (
          input.difficulty_coefficient !== undefined &&
          input.difficulty_coefficient !== before.difficulty_coefficient
        ) {
          existing.version = before.version + 1;
        }
        this.appendAudit(data, admin, 'UPDATE_EXERCISE', 'exercise', existing.id, before, {
          ...existing,
        });
        return existing;
      }

      const created: Exercise = {
        id: `ex-${input.code ?? Date.now()}`,
        code: input.code ?? `custom_${Date.now()}`,
        name_i18n: input.name_i18n ?? { es: '', en: '' },
        theme: input.theme ?? 'DYNAMIC',
        difficulty_coefficient: input.difficulty_coefficient ?? 1,
        reference_mark: input.reference_mark ?? 1,
        allows_weight: input.allows_weight ?? false,
        version: 1,
        active: input.active ?? true,
        demo_video_url: input.demo_video_url ?? null,
      };
      data.exercises.push(created);
      this.appendAudit(data, admin, 'CREATE_EXERCISE', 'exercise', created.id, null, created);
      return created;
    });

    return delay(result);
  }

  async adminUpsertSeason(input: UpsertSeasonInput): Promise<Season> {
    const admin = this.requireAdmin();

    const result = mutate((data) => {
      if (input.id) {
        const existing = data.seasons.find((s) => s.id === input.id);
        if (!existing) throw new RepositoryError('notFound');
        const before = { ...existing };
        Object.assign(existing, input);
        this.appendAudit(data, admin, 'UPDATE_SEASON', 'season', existing.id, before, {
          ...existing,
        });
        return existing;
      }

      const created: Season = {
        id: `season-${Date.now()}`,
        name_i18n: input.name_i18n ?? { es: '', en: '' },
        quarter: input.quarter ?? 1,
        year: input.year ?? new Date().getUTCFullYear(),
        start_date: input.start_date ?? nowIso(),
        end_date: input.end_date ?? nowIso(),
        carryover_pct: input.carryover_pct ?? 40,
        status: input.status ?? 'UPCOMING',
      };
      data.seasons.push(created);
      this.appendAudit(data, admin, 'CREATE_SEASON', 'season', created.id, null, created);
      return created;
    });

    return delay(result);
  }

  async adminCloseSeason(seasonId: string): Promise<void> {
    const admin = this.requireAdmin();

    mutate((data) => {
      const season = data.seasons.find((s) => s.id === seasonId);
      if (!season) throw new RepositoryError('notFound');

      // Insignia de campeon de temporada por categoria (RF-TEMP-04).
      const lambda = data.badges.find((b) => b.code === 'golden_lambda');
      if (lambda) {
        const byCategory = new Map<string, Profile[]>();
        for (const profile of data.profiles.filter((p) => p.onboarding_completed)) {
          const key = `${profile.gender_category}|${profile.weight_class}`;
          byCategory.set(key, [...(byCategory.get(key) ?? []), profile]);
        }
        for (const [, group] of byCategory) {
          const champion = [...group].sort((a, b) => b.season_points - a.season_points)[0];
          if (!champion || champion.season_points <= 0) continue;
          data.userBadges.push({
            id: `ub-lambda-${season.id}-${champion.id}`,
            user_id: champion.id,
            badge_id: lambda.id,
            competition_id: null,
            season_id: season.id,
            gender_category: champion.gender_category,
            weight_class: champion.weight_class,
            awarded_at: nowIso(),
            featured: false,
          });
        }
      }

      // Arrastre del 40 % y recalculo del rango (RF-TEMP-05 / RF-RANGO-04).
      for (const profile of data.profiles) {
        const carried = carryoverPoints(profile.season_points, season.carryover_pct);
        profile.season_points = carried;
        profile.rank_level = rankForPoints(carried).level;
        // El mejor rango historico no se toca (RF-TEMP-06).
      }

      season.status = 'CLOSED';
      const next = data.seasons.find((s) => s.status === 'UPCOMING');
      if (next) next.status = 'ACTIVE';

      this.appendAudit(data, admin, 'CLOSE_SEASON', 'season', season.id, { status: 'ACTIVE' }, {
        status: 'CLOSED',
        carryover_pct: season.carryover_pct,
      });
    });

    return delay(undefined);
  }

  async adminListUsers(search?: string): Promise<Profile[]> {
    this.requireAdmin();
    const needle = search?.trim().toLowerCase();
    const list = loadDemoData().profiles.filter(
      (p) => !needle || p.alias.toLowerCase().includes(needle),
    );
    return delay([...list].sort((a, b) => b.season_points - a.season_points).slice(0, 100));
  }

  async adminUpdateUser(input: AdminUserUpdate): Promise<Profile> {
    const admin = this.requireAdmin();
    if (!input.reason.trim()) throw new RepositoryError('required');

    const result = mutate((data) => {
      const profile = data.profiles.find((p) => p.id === input.userId);
      if (!profile) throw new RepositoryError('notFound');
      const before = { ...profile };

      if (input.role) profile.role = input.role;
      if (input.genderCategory) profile.gender_category = input.genderCategory;
      if (input.bodyWeightKg !== undefined) {
        profile.body_weight_kg = input.bodyWeightKg;
        profile.weight_class = assignWeightClass(input.bodyWeightKg, profile.gender_category);
      }
      if (input.weightClass) profile.weight_class = input.weightClass;

      // Expulsion por fraude: se invalidan resultados y se retiran insignias
      // (RF-ADM-09 / RF-INS-05).
      if (input.banned) {
        for (const submission of data.submissions.filter((s) => s.user_id === profile.id)) {
          submission.status = 'INVALIDADO';
          submission.final_score = null;
        }
        data.userBadges = data.userBadges.filter((b) => b.user_id !== profile.id);
        profile.season_points = 0;
        profile.rank_level = 1;
        profile.deleted_at = nowIso();
      }

      this.appendAudit(data, admin, 'UPDATE_USER', 'profile', profile.id, before, {
        ...profile,
        reason: input.reason,
      });
      return profile;
    });

    return delay(result);
  }

  async adminGrantBadge(userId: string, badgeId: string): Promise<void> {
    const admin = this.requireAdmin();
    mutate((data) => {
      data.userBadges.push({
        id: `ub-manual-${Date.now()}`,
        user_id: userId,
        badge_id: badgeId,
        competition_id: null,
        season_id: null,
        gender_category: null,
        weight_class: null,
        awarded_at: nowIso(),
        featured: false,
      });
      this.appendAudit(data, admin, 'GRANT_BADGE', 'user_badge', userId, null, { badgeId });
    });
    return delay(undefined);
  }

  async adminRevokeBadge(userBadgeId: string): Promise<void> {
    const admin = this.requireAdmin();
    mutate((data) => {
      const index = data.userBadges.findIndex((b) => b.id === userBadgeId);
      if (index < 0) throw new RepositoryError('notFound');
      const [removed] = data.userBadges.splice(index, 1);
      this.appendAudit(data, admin, 'REVOKE_BADGE', 'user_badge', userBadgeId, removed, null);
    });
    return delay(undefined);
  }

  async adminGetWeightClasses(): Promise<WeightClassThresholds> {
    return delay({
      M: WEIGHT_CLASSES.M.map((b) => ({ ...b })),
      F: WEIGHT_CLASSES.F.map((b) => ({ ...b })),
      minParticipants: CATEGORY_RULES.MIN_PARTICIPANTS_PER_CATEGORY,
    });
  }

  async adminUpdateWeightClasses(): Promise<void> {
    // En la demo los umbrales son de solo lectura: viven en el fichero de
    // configuracion. En Supabase se guardan en `app_settings`.
    this.requireAdmin();
    return delay(undefined);
  }

  async adminGetMetrics(): Promise<AdminMetrics> {
    this.requireAdmin();
    const data = loadDemoData();

    const currentFortnight = this.currentFortnightNumber(data);
    const roundCompetitionIds = new Set(
      data.competitions.filter((c) => c.fortnight_number === currentFortnight).map((c) => c.id),
    );

    const reviewed = data.submissions.filter((s) => s.reviewed_at);
    const avgReviewHours =
      reviewed.length === 0
        ? 0
        : reviewed.reduce((sum, s) => {
            const created = new Date(s.created_at).getTime();
            const done = new Date(s.reviewed_at!).getTime();
            return sum + Math.max(0, done - created) / 3600000;
          }, 0) / reviewed.length;

    const decided = data.submissions.filter(
      (s) => s.status === 'APROBADO' || s.status === 'RECHAZADO',
    );
    const rejected = decided.filter((s) => s.status === 'RECHAZADO');

    const byTheme = new Map<CompetitionTheme, number>();
    for (const submission of data.submissions) {
      const theme = data.competitions.find((c) => c.id === submission.competition_id)?.theme;
      if (!theme) continue;
      byTheme.set(theme, (byTheme.get(theme) ?? 0) + 1);
    }

    const byCategory = new Map<string, number>();
    for (const submission of data.submissions) {
      const key = `${submission.gender_category}|${submission.weight_class}`;
      byCategory.set(key, (byCategory.get(key) ?? 0) + 1);
    }

    const byCountry = new Map<string, number>();
    for (const profile of data.profiles) {
      if (!profile.country) continue;
      byCountry.set(profile.country, (byCountry.get(profile.country) ?? 0) + 1);
    }

    const activeUserIds = new Set(
      data.submissions
        .filter((s) => roundCompetitionIds.has(s.competition_id))
        .map((s) => s.user_id),
    );

    return delay({
      activeUsers: activeUserIds.size,
      totalUsers: data.profiles.filter((p) => !p.deleted_at).length,
      submissionsThisFortnight: data.submissions.filter((s) => roundCompetitionIds.has(s.competition_id))
        .length,
      pendingReview: data.submissions.filter(
        (s) => s.status === 'PENDIENTE' || s.status === 'EN_REVISION',
      ).length,
      avgReviewHours: Math.round(avgReviewHours * 10) / 10,
      rejectionRate: decided.length ? rejected.length / decided.length : 0,
      weekFourRetention: 0.31,
      submissionsByTheme: [...byTheme.entries()].map(([theme, count]) => ({ theme, count })),
      submissionsByCategory: [...byCategory.entries()].map(([key, count]) => {
        const [gender, weightClass] = key.split('|');
        return {
          gender: gender as GenderCategory,
          weightClass: weightClass as WeightClass,
          count,
        };
      }),
      topCountries: [...byCountry.entries()]
        .map(([country, count]) => ({ country, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 8),
    });
  }

  async adminListAuditLog(limit = 50): Promise<AuditLogEntry[]> {
    this.requireAdmin();
    return delay(loadDemoData().auditLog.slice(0, limit));
  }

  async adminGetComments(ids: string[]): Promise<SubmissionComment[]> {
    this.requireAdmin();
    const data = loadDemoData();
    return delay(data.submissionComments.filter((c) => ids.includes(c.id)));
  }

  async adminSetCommentHidden(commentId: string, hidden: boolean): Promise<void> {
    this.requireAdmin();
    mutate((data) => {
      const comment = data.submissionComments.find((c) => c.id === commentId);
      if (comment) comment.hidden = hidden;
    });
    void this.emit();
    return delay(undefined);
  }

  async adminSetSubmissionHidden(submissionId: string, hidden: boolean): Promise<void> {
    this.requireAdmin();
    mutate((data) => {
      const submission = data.submissions.find((s) => s.id === submissionId);
      if (submission) submission.hidden_at = hidden ? nowIso() : null;
    });
    void this.emit();
    return delay(undefined);
  }

  async adminGetForumPosts(ids: string[]): Promise<ForumPost[]> {
    this.requireAdmin();
    return delay(loadDemoData().forumPosts.filter((post) => ids.includes(post.id)));
  }

  async adminSetForumPostHidden(postId: string, hidden: boolean): Promise<void> {
    this.requireAdmin();
    mutate((data) => {
      const post = data.forumPosts.find((p) => p.id === postId);
      if (post) post.hidden = hidden;
    });
    void this.emit();
    return delay(undefined);
  }

  async adminResolveReport(reportId: string): Promise<void> {
    this.requireAdmin();
    mutate((data) => {
      const report = data.reports.find((r) => r.id === reportId);
      if (report) report.status = 'CLOSED';
    });
    void this.emit();
    return delay(undefined);
  }

  /**
   * En la demo no hay servicio de traduccion: se aplica un glosario minimo con
   * las palabras que mas salen en una competicion y lo demas se deja igual.
   * Sirve para probar el formulario, no para publicar.
   */
  async adminTranslate(text: string, _from: Language, to: Language): Promise<string> {
    const glosario: Array<[string, string]> = [
      ['dominadas', 'pull-ups'],
      ['fondos', 'dips'],
      ['flexiones', 'push-ups'],
      ['sentadillas', 'squats'],
      ['repeticiones', 'reps'],
      ['lastre', 'added weight'],
      ['peso corporal', 'body weight'],
      ['normas', 'rules'],
      ['quincena', 'fortnight'],
      ['barra', 'bar'],
      ['tiempo', 'time'],
      ['maximo', 'max'],
    ];

    let out = text;
    for (const [es, en] of glosario) {
      const [de, a] = to === 'en' ? [es, en] : [en, es];
      out = out.replace(new RegExp(de, 'gi'), a);
    }
    return delay(out);
  }

  async adminListReports(): Promise<Report[]> {
    this.requireAdmin();
    return delay(loadDemoData().reports);
  }
}

/** Se exporta para la pantalla de ajustes del modo demo. */
export const DEMO_WEIGHT_MULTIPLIER_MAX = WEIGHT_MULTIPLIER.MAX;
